/**
 * iter 53 / 63 — Reusable Customer create / edit drawer.
 *
 * Single source of truth for the "New Customer" / "Edit Customer" UX. Used by:
 *   • Organizations page → + NEW → Add Customer
 *   • New Job drawer    → + Customer
 *   • New Invoice drawer → + Customer
 *
 * Fields kept in lockstep with the richest version (Organizations) so every
 * surface gets the same capabilities (proof recipients, tag picker, etc).
 *
 * iter 63 — now uses the shared `useDrawerForm` lifecycle hook + sticky
 * `<DrawerFooter>` for dirty-guarded close and a uniform action bar
 * across every entity-manager drawer in the app.
 *
 * Props
 * -----
 *   open        bool   — drawer visibility
 *   onClose     fn     — called when drawer dismisses (cancel / backdrop)
 *   customer    obj|null — if set, drawer opens in EDIT mode; otherwise CREATE
 *   onSaved     fn(customer) — called with the saved row payload from the
 *                              backend so the parent can refresh lists, auto-
 *                              link the customer to a job/invoice, etc.
 *   title       string|null — override the default header text
 *   subtitle    string|null — small description line under the header
 *                              (e.g. "Creates a customer in your records.")
 */
import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Drawer, Field } from "./UI";
import { TagPicker } from "./TagUI";
import ProofRecipientsPicker from "./ProofRecipientsPicker";
import {
  ErrorBanner, DrawerFooter, useDrawerForm,
} from "./OrgAdmin/_managerShared";

const EMPTY = {
  name: "", email: "", phone: "", address: "",
  proof_recipients_json: [],
};

const hydrate = (rec) => ({
  name: rec.name || "",
  email: rec.email || "",
  phone: rec.phone || "",
  address: rec.address || "",
  proof_recipients_json: rec.proof_recipients_json || [],
});

export default function CustomerFormDrawer({
  open, onClose, customer = null, onSaved, title, subtitle,
}) {
  const isEdit = !!customer;
  const f = useDrawerForm({
    open, isEdit, initialData: customer, emptyForm: EMPTY, hydrate,
  });
  const { form, setForm, setField, busy, setBusy, err, setErr, requestClose } = f;

  // Tags + proof-recipients are heterogeneous shapes (arrays of objects),
  // so they're managed alongside `form` but explicitly cleared on open.
  const [tags, setTags] = useState([]);
  useEffect(() => {
    if (!open) return;
    setTags(customer?.tags || []);
  }, [open, customer]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        name: form.name.trim(),
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        proof_recipients_json: form.proof_recipients_json || [],
      };
      const r = isEdit
        ? await api.put(`/customers/${customer.id}`, body)
        : await api.post("/customers", body);
      const savedId = r.data?.id || customer?.id;
      if (tags.length && savedId) {
        try { await api.post(`/customer/${savedId}/tags`, { tag_ids: tags.map((t) => t.id) }); }
        catch (tagErr) { console.warn("tag save failed", tagErr); }
      }
      onSaved?.(r.data || { ...customer, ...body });
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => requestClose(onClose)}
      title={title || (isEdit ? "Edit customer" : "New customer")}
      size="narrow"
      testid="customer-form-drawer"
      footer={
        <DrawerFooter
          testid="customer-form-footer"
          onCancel={() => requestClose(onClose)}
          onSave={save}
          mode={isEdit ? "edit" : "create"}
          busy={busy}
          saveLabel={isEdit ? "Save" : "Create customer"}
          saveDisabled={!form.name.trim()}
        />
      }
    >
      <ErrorBanner err={err} testid="customer-form-error" />
      {subtitle && (
        <p className="text-xs text-muted-foreground mb-3" data-testid="customer-form-subtitle">{subtitle}</p>
      )}
      <Field label="NAME">
        <input
          className="input"
          autoFocus
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          data-testid="customer-form-name-input"
        />
      </Field>
      <Field label="EMAIL">
        <input
          className="input"
          type="email"
          value={form.email}
          onChange={(e) => setField("email", e.target.value)}
          data-testid="customer-form-email-input"
        />
      </Field>
      <Field label="PHONE">
        <input
          className="input"
          value={form.phone}
          onChange={(e) => setField("phone", e.target.value)}
          data-testid="customer-form-phone-input"
        />
      </Field>
      <Field label="ADDRESS">
        <textarea
          className="input"
          rows={2}
          value={form.address}
          onChange={(e) => setField("address", e.target.value)}
          data-testid="customer-form-address-input"
        />
      </Field>
      <Field label="PROOF-OF-SERVICE RECIPIENTS">
        <ProofRecipientsPicker
          value={form.proof_recipients_json}
          onChange={(v) => setForm((cur) => ({ ...cur, proof_recipients_json: v }))}
          testid="customer-form-proof-recipients"
        />
      </Field>
      <TagPicker
        category="customer"
        value={tags}
        onChange={setTags}
        testid="customer-form-tag-picker"
      />
    </Drawer>
  );
}
